import { isPrivateProductMediaKey, operatorMediaSrc } from "@/lib/media";
import { mediaInheritanceMode } from "@/lib/phase1-sync-contract";

export type ListingMediaSource = "listing_custom" | "product" | "variant" | "option_value" | string;

export type EffectiveListingMediaRow = {
  media_asset_id: string;
  storage_key: string | null;
  original_filename: string | null;
  mime_type?: string | null;
  role?: string | null;
  position?: number | null;
  is_primary?: boolean | null;
  source?: ListingMediaSource | null;
  alt_text?: string | null;
};

export type ListingMediaAuthority = "product_master" | "channel_override";

export type ListingMediaPreviewCard = {
  media_asset_id: string;
  storage_key: string | null;
  original_filename: string | null;
  mime_type: string | null;
  isPrimary: boolean;
  galleryKind: "primary" | "additional";
  authority: ListingMediaAuthority;
  sources: ListingMediaSource[];
  alsoOnProductMaster: boolean;
  alsoOnListing: boolean;
  duplicatePresentation: boolean;
};

export function listingMediaOpenMutations(): string[] {
  return [];
}

export function listingMediaProviderWrites(): string[] {
  return [];
}

function sourceRank(source: ListingMediaSource | null | undefined): number {
  if (source === "listing_custom") return 0;
  if (source === "variant" || source === "option_value") return 1;
  return 2;
}

/**
 * One card per asset. listing_override keeps the channel primary; inherit uses
 * Product Master primary. Duplicate listing+product rows of the same asset are
 * presentation-only — both records stay.
 */
export function listingMediaPreviewCards(opts: {
  mediaStrategy: string | null | undefined;
  rows: EffectiveListingMediaRow[];
}): ListingMediaPreviewCard[] {
  const inherit = mediaInheritanceMode(opts.mediaStrategy) === "inherit";
  const groups = new Map<string, EffectiveListingMediaRow[]>();
  for (const row of opts.rows) {
    const id = String(row.media_asset_id || "").trim();
    if (!id) continue;
    const list = groups.get(id) ?? [];
    list.push(row);
    groups.set(id, list);
  }

  const cards: ListingMediaPreviewCard[] = [];
  for (const [id, group] of groups) {
    const sorted = [...group].sort(
      (a, b) => sourceRank(a.source) - sourceRank(b.source) || Number(b.is_primary) - Number(a.is_primary) || Number(a.position ?? 0) - Number(b.position ?? 0),
    );
    const listingRow = sorted.find((r) => r.source === "listing_custom");
    const productRow = sorted.find((r) => r.source === "product");
    const chosen = inherit ? (productRow ?? listingRow ?? sorted[0]) : (listingRow ?? productRow ?? sorted[0]);
    const sources = [...new Set(sorted.map((r) => (r.source || "product") as ListingMediaSource))];
    const alsoOnProductMaster = sources.includes("product");
    const alsoOnListing = sources.includes("listing_custom");
    const authority: ListingMediaAuthority =
      !inherit && listingRow ? "channel_override" : "product_master";
    const isPrimary = inherit
      ? productRow?.is_primary === true
      : listingRow
        ? listingRow.is_primary === true
        : productRow?.is_primary === true;
    cards.push({
      media_asset_id: id,
      storage_key: chosen?.storage_key ?? null,
      original_filename: chosen?.original_filename ?? listingRow?.original_filename ?? productRow?.original_filename ?? null,
      mime_type: chosen?.mime_type ?? null,
      isPrimary,
      galleryKind: isPrimary ? "primary" : "additional",
      authority,
      sources,
      alsoOnProductMaster,
      alsoOnListing,
      duplicatePresentation: alsoOnListing && alsoOnProductMaster,
    });
  }

  cards.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || (a.original_filename || "").localeCompare(b.original_filename || ""));
  if (!inherit) {
    const primaryId = cards.find((c) => c.authority === "channel_override" && c.isPrimary)?.media_asset_id;
    if (primaryId) {
      for (const c of cards) {
        if (c.media_asset_id !== primaryId && c.isPrimary) {
          c.isPrimary = false;
          c.galleryKind = "additional";
        }
      }
    }
  }
  return cards;
}

export function intendedChannelMediaSyncPreview(opts: {
  mediaStrategy: string | null | undefined;
  cards: ListingMediaPreviewCard[];
}): { willWriteProvider: boolean; reason: string; primaryFilename: string | null; filenames: string[] } {
  const inherit = mediaInheritanceMode(opts.mediaStrategy) === "inherit";
  const filenames = opts.cards.map((c) => c.original_filename || c.media_asset_id);
  const primary = opts.cards.find((c) => c.isPrimary)?.original_filename ?? null;
  if (!inherit) {
    return {
      willWriteProvider: false,
      reason: "listing_override: Media-domain sync preserves channel media and does not write the provider.",
      primaryFilename: primary,
      filenames,
    };
  }
  return {
    willWriteProvider: false,
    reason: "Media-domain sync is preview-only in this pass. These are the Product Master images inherit would use.",
    primaryFilename: primary,
    filenames,
  };
}

export function operatorPreviewStatus(
  storageKey: string | null | undefined,
  signedByKey: Record<string, string>,
): { kind: "ready" | "missing"; src: string | null; message: string | null } {
  if (!storageKey) {
    return { kind: "missing", src: null, message: "No storage key on this media record." };
  }
  if (isPrivateProductMediaKey(storageKey)) {
    const signed = signedByKey[storageKey];
    if (signed) return { kind: "ready", src: signed, message: null };
    return { kind: "missing", src: null, message: "Private image could not be signed for preview." };
  }
  const remote = operatorMediaSrc(storageKey);
  if (remote) return { kind: "ready", src: remote, message: null };
  return { kind: "missing", src: null, message: "Image URL is missing or not previewable." };
}

export function useProductMasterIsExplicitClick(trigger: "open_screen" | "operator_click"): boolean {
  return trigger === "operator_click";
}
